import useResizeObserver from '@react-hook/resize-observer'
import ScratchBlockSvgFilters from 'components/Project/Project/ScratchBlockSvgFilters/ScratchBlockSvgFilters'
import { Banner } from 'components/shared/Banner/Banner'
import CoolestProjects from 'components/shared/CoolestProjects/CoolestProjects'
import EditorSlice from 'components/shared/EditorSlice/EditorSlice'
import HtmlHead from 'components/shared/HtmlHead/HtmlHead'
import { getPathwayDetails, transitionName } from 'helpers/Project'
import {
  isKnowledgeQuiz,
  prismInit,
  projectHasBadge,
  stepInit,
} from 'helpers/Step'
import config from 'helpers/config'
import { renderMakecodeBlocks } from 'helpers/makecode'
import { scratchblocksInit } from 'helpers/scratchblocks'
import { withRouter } from 'helpers/withRouter'
import path from 'path-browserify'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef } from 'react'
import React, { useState } from 'react'
import { withNamespaces } from 'react-i18next'
import { connect } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import KnowledgeQuiz from './KnowledgeQuiz/KnowledgeQuiz'
import StepContent from './StepContent/StepContent'

const childFactoryCreator = (slideDirection) => (child) =>
  React.cloneElement(child, {
    classNames: transitionName(slideDirection),
  })

const cssTransitionConfig = () => ({
  enter: config.transitionEnterTimeout,
  exit: config.transitionExitTimeout,
})
const isEditorStarterProject = (project) => !!project?.editorStarterProject
const isScratchProject = (project) => project.software.indexOf('scratch') !== -1

const coolestEnds = new Date('05/28/2025')

const isCoolestOn = (coolestEnds) => {
  const today = new Date()

  return today <= coolestEnds
}

const notAllowedProjects = [
  'astro-pi-mission-zero',
  'mission-space-lab-creator-guide',
  'safeguarding-module',
  'getting-started-with-the-sense-hat',
  'image-id-coral',
  'astro-pi-flight-case-mk2',
  'sense-hat-data-logger',
  'astropi-iss-speed',
  'translating-for-raspberry-pi',
  'recruiting-volunteers',
  'dojo-mentoring',
  'xai-challenge-startup',
  'xai-challenge-image-classifier',
  'xai-challenge-submit',
  'xai-challenge-audio-classifier',
  'xai-challenge-text-classifier',
  'code-editor',
]

const isAllowedWithCoolestProjects = (slug) =>
  !notAllowedProjects.map((project) => slug === project).includes(true)

const StepContainer = ({
  badge,
  currentStepPosition,
  project,
  slideDirection,
  slug,
  currentLocale,
  progressCallback,
  router: { location, navigate },
  t,
}) => {
  const contentRef = useRef(null)
  const searchParams = useSearchParams()[0]
  const branch = searchParams.get('branch') || ''
  const trackLastStep = useCallback(() => {
    if (currentStepPosition !== project.steps.length - 1) {
      return
    }

    window.ga &&
      window.ga('send', {
        hitType: 'event',
        eventCategory: project.title,
        eventAction: 'Reached final step',
      })
  }, [currentStepPosition, project.steps, project.title])

  const scrollTo = (position = 0) => {
    let interProjectNav = false
    if (location.state?.prevPath) {
      interProjectNav =
        path.normalize(path.join(location.pathname, '..')) ===
        path.normalize(path.join(location.state?.prevPath, '..'))
    }
    const animate =
      window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === false
    const behavior = animate ? 'smooth' : 'auto'

    if (position === 0 && !interProjectNav) {
      window.scrollTo({ top: 0, behavior })
    } else {
      contentRef.current.scrollIntoView({
        behavior,
        block: 'start',
        inline: 'nearest',
      })
    }
  }

  const [hasScrolled, setHasScrolled] = useState(false)

  useResizeObserver(contentRef, () => {
    if (!hasScrolled) {
      scrollTo(currentStepPosition)
      setHasScrolled(true)
    }
  })

  useEffect(() => {
    setHasScrolled(false)
  }, [currentStepPosition])

  useEffect(() => {
    // This is a workaround, if the locale has no corresponding folder in /public/locales
    // then the dom isn't loaded at this point and the init fails.
    // this waits until the dom is rendered before trying to initialize events
    setTimeout(() => {
      prismInit()
      stepInit()
      scratchblocksInit(project, currentLocale)
      renderMakecodeBlocks()
    }, 0)

    trackLastStep()
  }, [currentLocale, project, trackLastStep])

  const firstStepLocation = `/${currentLocale}/projects/${slug}/0`
  const matchFirstStepLocation =
    window.location.href.indexOf(firstStepLocation) !== -1
  let step = project.steps[currentStepPosition]
  if (!step && matchFirstStepLocation) {
    // If on first step, just use that
    step = project.steps[0]
  }
  if (!step) {
    navigate(firstStepLocation)
    return <></>
  }

  const { metaDescription, metaTitle } = project
  let title = metaTitle
  if (!title) {
    let software = ''
    if (project.software && project.software.length) {
      software =
        ' | ' +
        project.software
          .map((tag) => tag.charAt(0).toLocaleUpperCase() + tag.slice(1))
          .join(', ')
    }

    title = `${project.title}${software} | ${t('meta.project.titleSuffix')}`
    if (currentStepPosition) {
      title = `${step.title} | ${title}`
    }
  }

  const TopContent = () => (
    <>
      {isEditorStarterProject(project) && (
        <EditorSlice slug={slug} locale={currentLocale} />
      )}
      {branch && <Banner text={`${t('dev.branch_banner')}: ${branch}`} />}
      {isScratchProject(project) && <ScratchBlockSvgFilters />}
    </>
  )

  const BottomContent = () => (
    <>
      {isCoolestOn(coolestEnds) &&
        isAllowedWithCoolestProjects(slug) &&
        (currentStepPosition === project.steps.length - 1 ||
          currentStepPosition === 0) && (
          <CoolestProjects endDate={coolestEnds} />
        )}
    </>
  )

  return (
    <>
      <HtmlHead
        title={title}
        description={metaDescription || project.description}
        image={project.heroImage}
      />
      <section className="c-project__content" ref={contentRef}>
        <TopContent />
        <div className="c-project-steps">
          <TransitionGroup childFactory={childFactoryCreator(slideDirection)}>
            <CSSTransition
              key={step.position}
              classNames={transitionName(slideDirection)}
              timeout={cssTransitionConfig()}
            >
              {isKnowledgeQuiz(step) ? (
                <KnowledgeQuiz
                  badge={badge}
                  slug={slug}
                  locale={currentLocale}
                  quizPath={step.knowledgeQuiz}
                  position={step.position}
                  project={project}
                  progressCallback={progressCallback}
                />
              ) : (
                <StepContent
                  slug={slug}
                  locale={currentLocale}
                  step={step}
                  archived={project.archived}
                  hasBadge={projectHasBadge(project)}
                  badge={badge}
                  projectTitle={project.title}
                  progressCallback={progressCallback}
                  pathway={getPathwayDetails(project.included)}
                  pathwayStep={project.pathwayStep}
                />
              )}
            </CSSTransition>
          </TransitionGroup>
          {branch && <Banner text={`${t('dev.branch_banner')}: ${branch}`} />}
        </div>
        <BottomContent />
      </section>
    </>
  )
}

StepContainer.propTypes = {
  badge: PropTypes.object,
  currentStepPosition: PropTypes.number.isRequired,
  project: PropTypes.object.isRequired,
  slideDirection: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  currentLocale: PropTypes.string.isRequired,
  progressCallback: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
  const { currentStepPosition, project, slideDirection, slug, currentLocale } =
    state.project
  const { badge } = state
  return {
    badge,
    currentStepPosition,
    project,
    slideDirection,
    slug,
    currentLocale,
  }
}

export {
  StepContainer,
  isScratchProject,
  isEditorStarterProject,
  isAllowedWithCoolestProjects,
}
export default connect(mapStateToProps)(
  withRouter(withNamespaces('translations')(StepContainer))
)
